import { ref, type ComputedRef } from 'vue';
import { ElMessage } from 'element-plus';
import { fetchQuotePdfBlob, triggerBlobDownload } from '@/services/quotePdfService';
import { maintainExpandMode } from './expandMode';
import { mapFormaPago, mapPaquete } from './paymentMaps';
import type { PlanSelection, QuoteSources } from './types';
import { resolveIdCotizacion } from './quoteId';

interface DownloadDeps {
  sources: QuoteSources;
  plan: PlanSelection;
  isChubbSelected: ComputedRef<boolean>;
}

export function useQuoteDownload({ sources, plan, isChubbSelected }: DownloadDeps) {
  const isDownloading = ref(false);

  const downloadPdf = async (idCotizacion: number): Promise<void> => {
    const pdfBlob = await fetchQuotePdfBlob({
      idCotizacion,
      formaPago: mapFormaPago(plan.selectedPaymentMethod.value),
      paquete: mapPaquete(plan.selectedPlanType.value),
    });
    triggerBlobDownload(pdfBlob, `Cotizacion-${idCotizacion}.pdf`);
    ElMessage.success({ message: 'PDF descargado correctamente', duration: 3000, showClose: true });
  };

  const handleDownload = async (): Promise<void> => {
    if (isChubbSelected.value) {
      ElMessage.info('La descarga de PDF no está disponible para Chubb en este momento.');
      return;
    }
    const idCotizacion = resolveIdCotizacion(sources);
    if (!idCotizacion) {
      ElMessage.warning('No se puede descargar: ID de cotización no disponible');
      return;
    }

    isDownloading.value = true;
    try {
      await downloadPdf(idCotizacion);
    } catch (error) {
      ElMessage.error({
        message: error instanceof Error ? error.message : 'Error al descargar el PDF',
        duration: 4000,
        showClose: true,
      });
    } finally {
      isDownloading.value = false;
      maintainExpandMode();
    }
  };

  return { isDownloading, handleDownload };
}
